/**
 * ピン編集シート（旧 METER 画面）
 *
 * - 縮小写真＋ピン、ラベル
 * - 測光値の入力：EV 値 ／ L-858D シャッター速度（F 優先・反射 1°）
 *   操作：ドラム（上下にフリック）／ダイヤル（親指で左右に回す）。± は長押しで連続
 *   L-858D：ISO（このシーン）、絞り（f/11 風景・f/8 人物＋任意）、メーターの段、1/10 段表示
 * - 配置ゾーン（なぞって選択）＋「早見表」（シートの上に重ねて開く）
 * - 役：なし／主要シャドウ／主要ハイライト（各 1 本まで）
 * - 下端「決定」。右上で削除（新規のピンは閉じるだけ）
 *
 * 保存は従来どおり meterEV（ISO 100 の EV）。L-858D の表示値は reading に残す。
 */

import {
  openSheet, toast, escapeHtml, icon, buzz, buildZoneScale, zoneToRoman,
  ZONE_NAMES, ZONE_EXAMPLES, confirmDialog, holdRepeat, blobUrl, releaseUrls,
} from './ui.js';
import { getCurrentShoot, getSpot, updateSpot, removeSpot, updateCurrentShoot, committedSpots, pushSpotUndo } from './scene.js';
import { prefs } from './prefs.js';
import {
  SSL, SS_MAX, APL, AP_MIN, AP_MAX, isoIndexOf, isoLabel, ISO_K_MIN, ISO_K_MAX,
  apertureIndexOf, stepSize, tenthMax, splitEv, evFrom, bumpSek, conversionLine,
} from './sekonic.js';
import { APERTURES, SHUTTERS } from './zone-calc.js';

const ROW = 40;     // ドラムの 1 目盛りの高さ (px)
const DEG = 1.8;    // ダイヤルの 1 目盛りの角度
const PX_PER_DEG = 7.85;

let sheet = null;
let st = null; // 編集中の値

export function openMeter(spotId, opts = {}) {
  const shoot = getCurrentShoot();
  const spot = getSpot(spotId);
  if (!shoot || !spot) { toast('ピンが見つかりません'); return; }
  const idx = committedSpots(shoot).findIndex((s) => s.id === spotId);
  const n = idx >= 0 ? idx + 1 : committedSpots(shoot).length + 1;
  const rd = spot.reading && spot.reading.mode === 'sek' ? spot.reading : null;
  st = {
    id: spotId,
    isNew: !!opts.isNew || !!spot._draft,
    n,
    label: spot.label || '',
    ev: Number(spot.meterEV),
    zone: spot.placedZone,
    role: spot.role || null,
    mode: rd ? 'sek' : prefs.meterInput,
    style: prefs.inputStyle,
    ap: apertureIndexOf(rd?.aperture ?? prefs.sekAperture),
    sekStep: rd?.step === '1/3' ? '1/3' : (rd ? '1' : prefs.sekStep),
    tenth: rd ? (rd.tenths != null) : prefs.sekTenth,
    committed: false,
  };

  const body = document.createElement('div');
  body.innerHTML = `
    <div class="meter-top">
      <div class="meter-thumb photo"><img src="${blobUrl('meter', shoot.monoImageBlob || shoot.croppedImageBlob)}" alt="" /><span class="meter-thumb__pin" style="left:${spot.x * 100}%;top:${spot.y * 100}%"></span></div>
      <label style="flex:1;display:block"><span class="field__label">ラベル</span><input class="input" id="m-label" value="${escapeHtml(st.label)}" placeholder="例：影の岩肌／雪の表面" /></label>
    </div>
    <div class="chips" id="m-labels" style="padding:8px var(--gutter) 0">
      ${prefs.pinLabels.map((l) => `<button type="button" class="chip" data-lbl="${escapeHtml(l)}" style="height:32px">${escapeHtml(l)}</button>`).join('')}
    </div>
    <div class="meter-sec">
      <div class="meter-sec__head"><span>測光値の入力</span>
        <div class="seg seg--sm" id="m-style">
          <button type="button" class="seg__opt" data-style="drum">ドラム</button>
          <button type="button" class="seg__opt" data-style="arc">ダイヤル</button>
        </div>
      </div>
      <div class="seg seg--slide" id="m-mode" style="height:38px;margin-top:8px">
        <span class="seg__thumb" style="width:50%"></span>
        <button type="button" class="seg__opt" data-mode="ev" style="font-size:12.5px">EV 値</button>
        <button type="button" class="seg__opt" data-mode="sek" style="font-size:12.5px">L-858D シャッター速度</button>
      </div>
      <div id="m-sek"></div>
      <div id="m-input"></div>
      <div class="ev-foot"><span id="m-hint"></span><b id="m-conv"></b></div>
      <label class="ev-direct" id="m-direct"><span>EV を数字で入力</span><input class="input en" id="m-ev-input" type="number" inputmode="decimal" step="0.1" min="-6" max="24" /></label>
    </div>
    <div class="meter-sec" style="padding-top:12px">
      <div class="meter-sec__head" style="align-items:baseline"><span>配置ゾーン</span><button type="button" id="m-zc" style="color:var(--color-ink);text-decoration:underline;text-underline-offset:3px;padding:6px 0;font:500 12px/1 var(--font-jp-sans)">早見表</button></div>
      <div id="m-zone" style="margin-top:8px"></div>
      <div id="m-zlabels"></div>
      <div class="zname" id="m-zname"></div>
    </div>
    <div class="meter-sec">
      <div class="meter-sec__head"><span>役割</span></div>
      <div class="seg seg--slide" id="m-role" style="height:48px;margin-top:8px">
        <span class="seg__thumb" style="width:33.333%"></span>
        <button type="button" class="seg__opt" data-role="" style="font-size:13px">なし</button>
        <button type="button" class="seg__opt" data-role="shadow-anchor" style="font-size:13px">主要シャドウ</button>
        <button type="button" class="seg__opt" data-role="highlight-anchor" style="font-size:13px">主要ハイライト</button>
      </div>
    </div>
  `;
  const foot = document.createElement('div');
  // 5. 確定済みのピンを順に入力するときの「決定して次へ」
  const committed = committedSpots(shoot);
  const nextSpot = !st.isNew && idx >= 0 ? committed[idx + 1] : null;
  foot.innerHTML = nextSpot
    ? `<div style="display:grid;grid-template-columns:1fr 1.3fr;gap:8px"><button class="btn-primary" type="button" id="m-commit" style="font-size:16px;background:transparent;color:var(--color-ink);box-shadow:inset 0 0 0 1px var(--color-ink)">${icon('check')}決定</button><button class="btn-primary" type="button" id="m-next" style="font-size:16px">決定して次へ ${icon('arrow', 18)}</button></div>`
    : `<button class="btn-primary" type="button" id="m-commit" style="font-size:17px">${icon('check')}決定</button>`;

  sheet = openSheet({
    en: st.isNew ? 'new spot' : `spot ${n}`,
    title: st.isNew ? `新しいスポット ${n}` : `スポット ${n}`,
    body,
    foot,
    full: true,
    ruled: false,
    headExtra: `<button class="bar__btn" type="button" id="m-delete" aria-label="ピンを削除">${icon('trash')}</button>`,
    onClose: () => {
      releaseUrls('meter');
      // 「決定」せずに閉じた新規のピンは消す
      const s = getSpot(st.id);
      if (s && s._draft && !st.committed) removeSpot(st.id);
    },
  });

  const $ = (s) => sheet.el.querySelector(s);
  $('#m-label').addEventListener('input', (e) => { st.label = e.target.value; });

  sheet.el.querySelectorAll('[data-style]').forEach((b) => b.addEventListener('click', () => {
    st.style = b.dataset.style;
    prefs.inputStyle = st.style;
    buzz(6);
    renderAll();
  }));
  sheet.el.querySelectorAll('[data-mode]').forEach((b) => b.addEventListener('click', () => {
    st.mode = b.dataset.mode;
    prefs.meterInput = st.mode;
    buzz(6);
    renderAll();
  }));
  sheet.el.querySelectorAll('[data-role]').forEach((b) => b.addEventListener('click', () => {
    st.role = b.dataset.role || null;
    buzz(6);
    paintSeg('#m-role', '[data-role]', (x) => (x.dataset.role || null) === st.role);
  }));

  buildZoneScale($('#m-zone'), st.zone, (z) => { st.zone = z; paintZoneName(); }, { labels: $('#m-zlabels') });
  paintZoneName();
  paintSeg('#m-role', '[data-role]', (x) => (x.dataset.role || null) === st.role);

  // 旧版と同じく EV を数字で直接入力できる（−6〜24）
  const direct = $('#m-ev-input');
  direct.addEventListener('change', () => {
    const v = parseFloat(direct.value);
    if (Number.isFinite(v)) {
      st.ev = Math.max(-6, Math.min(24, Math.round(v * 100) / 100));
      paintValue();
    }
  });
  $('#m-zc').addEventListener('click', () => import('./zone-chart.js').then((m) => m.openZoneChart({ overlay: true })));
  $('#m-commit').addEventListener('click', () => commit());
  $('#m-next')?.addEventListener('click', () => commit(nextSpot.id));
  sheet.el.querySelectorAll('[data-lbl]').forEach((b) => b.addEventListener('click', () => {
    st.label = b.dataset.lbl;
    $('#m-label').value = st.label;
    buzz(6);
  }));
  $('#m-delete').addEventListener('click', onDelete);

  renderAll();
}

function paintSeg(sel, itemSel, isOn) {
  const seg = sheet.el.querySelector(sel);
  const items = [...seg.querySelectorAll(itemSel)];
  let idx = -1;
  items.forEach((b, i) => { const on = isOn(b); b.classList.toggle('is-on', on); if (on) idx = i; });
  const thumb = seg.querySelector('.seg__thumb');
  if (thumb) {
    thumb.style.left = `${(Math.max(0, idx) * 100) / items.length}%`;
    thumb.style.opacity = idx < 0 ? '0' : '1';
  }
}

function paintZoneName() {
  const el = sheet.el.querySelector('#m-zname');
  el.textContent = `${zoneToRoman(st.zone)}　${ZONE_NAMES[st.zone]} — ${ZONE_EXAMPLES[st.zone]}`;
}

// --------------------------------------------------
// 測光値の入力
// --------------------------------------------------
function sekOpts() {
  const shoot = getCurrentShoot();
  return {
    ap: st.ap,
    isoK: isoIndexOf(shoot?.meterIso ?? shoot?.ei ?? 100),
    sekStep: st.sekStep,
    tenth: st.tenth,
  };
}

/** 位置（ドラム・ダイヤルの目盛り番号）と値の対応 */
function scale() {
  if (st.mode === 'sek') {
    const S = stepSize(st.sekStep);
    const max = Math.floor(SS_MAX / S);
    return {
      count: max + 1,
      pos: () => {
        const { ib } = splitEv(st.ev, sekOpts());
        return Math.max(0, Math.min(max, Math.round((SS_MAX - ib) / S)));
      },
      set: (q) => {
        const o = sekOpts();
        const { t } = splitEv(st.ev, o);
        st.ev = evFrom(SS_MAX - q * S, t, o);
      },
      label: (q) => SSL[SS_MAX - q * S],
      short: (q) => {
        const v = SSL[SS_MAX - q * S];
        return v.startsWith('1/') ? v.slice(2) : `${v}s`;
      },
      major: (q) => (S === 3 ? q % 2 === 0 : q % 3 === 0),
    };
  }
  return {
    count: 91,
    pos: () => Math.max(0, Math.min(90, Math.round((st.ev + 6) * 3))),
    set: (q) => { st.ev = q / 3 - 6; },
    label: (q) => (q / 3 - 6).toFixed(1),
    short: (q) => String(Math.round(q / 3 - 6)),
    major: (q) => q % 3 === 0,
  };
}

function renderAll() {
  paintSeg('#m-style', '[data-style]', (b) => b.dataset.style === st.style);
  paintSeg('#m-mode', '[data-mode]', (b) => b.dataset.mode === st.mode);
  renderSekRow();
  renderInput();
  paintValue();
}

function renderSekRow() {
  const box = sheet.el.querySelector('#m-sek');
  if (st.mode !== 'sek') { box.innerHTML = ''; return; }
  const o = sekOpts();
  box.innerHTML = `
    <div class="sek-row">
      <span class="sek-row__lead">L-858D<br><small>F 優先 · 反射 1°</small></span>
      <span class="sek-row__lbl">ISO<br>このシーン</span>
      <span class="mini-step"><button type="button" data-iso="-1" aria-label="ISO を下げる">${icon('chev-l', 12)}</button><span class="num">${isoLabel(o.isoK)}</span><button type="button" data-iso="1" aria-label="ISO を上げる">${icon('chev-r', 12)}</button></span>
    </div>
    <div class="sek-row">
      <button type="button" class="pill ${st.ap === 21 ? 'is-on' : ''}" data-ap-set="21">f/11 風景</button>
      <button type="button" class="pill ${st.ap === 18 ? 'is-on' : ''}" data-ap-set="18">f/8 人物</button>
      <span class="en sub" style="margin-left:auto;font-size:10px">f/</span>
      <span class="mini-step"><button type="button" data-ap="-1" aria-label="絞りを開く">${icon('chev-l', 12)}</button><span class="num">${APL[st.ap]}</span><button type="button" data-ap="1" aria-label="絞りを絞る">${icon('chev-r', 12)}</button></span>
    </div>
    <div class="sek-row">
      <span class="sek-row__lbl" style="margin-right:auto;text-align:left">メーターの段</span>
      <div class="seg seg--md"><button type="button" class="seg__opt ${st.sekStep === '1' ? 'is-on' : ''}" data-step="1">1 段</button><button type="button" class="seg__opt ${st.sekStep === '1/3' ? 'is-on' : ''}" data-step="1/3">⅓ 段</button></div>
      <button type="button" class="pill pill--sm ${st.tenth ? 'is-on' : ''}" data-tenth>1/10 段</button>
    </div>
  `;
  box.querySelectorAll('[data-iso]').forEach((b) => b.addEventListener('click', () => {
    const k = Math.max(ISO_K_MIN, Math.min(ISO_K_MAX, o.isoK + Number(b.dataset.iso)));
    updateCurrentShoot({ meterIso: Number(isoLabel(k)) });
    buzz(6);
    renderAll();
  }));
  box.querySelectorAll('[data-ap-set]').forEach((b) => b.addEventListener('click', () => { st.ap = Number(b.dataset.apSet); buzz(6); renderAll(); }));
  box.querySelectorAll('[data-ap]').forEach((b) => b.addEventListener('click', () => {
    st.ap = Math.max(AP_MIN, Math.min(AP_MAX, st.ap + Number(b.dataset.ap)));
    buzz(6);
    renderAll();
  }));
  box.querySelectorAll('[data-step]').forEach((b) => b.addEventListener('click', () => {
    st.sekStep = b.dataset.step;
    prefs.sekStep = st.sekStep;
    buzz(6);
    renderAll();
  }));
  box.querySelector('[data-tenth]').addEventListener('click', () => {
    st.tenth = !st.tenth;
    prefs.sekTenth = st.tenth;
    buzz(6);
    renderAll();
  });
}

let drumUpdate = null;

function renderInput() {
  const box = sheet.el.querySelector('#m-input');
  const sc = scale();
  const showTenth = st.mode === 'sek' && st.tenth;
  if (st.style === 'drum') {
    box.innerHTML = `
      <div class="ev-in">
        <button class="ev-pm" type="button" data-pm="-1" aria-label="減らす">${icon('minus', 22)}</button>
        <div class="drum" id="m-drum"><span class="drum__win"></span></div>
        ${showTenth ? '<div class="drum drum--tenth" id="m-tenth"><span class="drum__win"></span></div>' : ''}
        <button class="ev-pm" type="button" data-pm="1" aria-label="増やす">${icon('plus', 22)}</button>
      </div>`;
    const drum = box.querySelector('#m-drum');
    const items = [];
    for (let q = 0; q < sc.count; q++) {
      const it = document.createElement('span');
      it.className = 'drum__item';
      it.textContent = sc.label(q);
      drum.appendChild(it);
      items.push(it);
    }
    const tdrum = box.querySelector('#m-tenth');
    const titems = [];
    if (tdrum) {
      for (let q = 0; q <= tenthMax(st.sekStep); q++) {
        const it = document.createElement('span');
        it.className = 'drum__item';
        it.textContent = String(q);
        tdrum.appendChild(it);
        titems.push(it);
      }
    }
    drumUpdate = () => {
      const p = sc.pos();
      items.forEach((it, q) => {
        const off = q - p;
        const a = Math.abs(off);
        it.style.transform = `translateY(${-off * ROW}px) rotateX(${off * 16}deg)`;
        it.style.opacity = a > 3 ? '0' : String(1 - a * 0.27);
        it.style.fontSize = a === 0 ? '28px' : '18px';
        it.style.fontWeight = a === 0 ? '400' : '300';
      });
      if (tdrum) {
        const t = splitEv(st.ev, sekOpts()).t;
        titems.forEach((it, q) => {
          const off = q - t;
          const a = Math.abs(off);
          it.style.transform = `translateY(${-off * ROW}px)`;
          it.style.opacity = a > 2 ? '0' : String(1 - a * 0.4);
          it.style.fontSize = a === 0 ? '22px' : '15px';
          it.style.fontWeight = '500';
        });
      }
    };
    bindVerticalDrag(drum, () => sc.pos(), (np) => { sc.set(Math.max(0, Math.min(sc.count - 1, np))); });
    drum.addEventListener('wheel', (e) => {
      e.preventDefault();
      sc.set(Math.max(0, Math.min(sc.count - 1, sc.pos() + (e.deltaY > 0 ? -1 : 1))));
      buzz(4);
      paintValue();
    }, { passive: false });
    if (tdrum) {
      bindVerticalDrag(tdrum, () => splitEv(st.ev, sekOpts()).t, (nt) => {
        const o = sekOpts();
        const { ib } = splitEv(st.ev, o);
        st.ev = evFrom(ib, Math.max(0, Math.min(tenthMax(st.sekStep), nt)), o);
      });
    }
  } else {
    box.innerHTML = `
      <div class="ev-in">
        <button class="ev-pm" type="button" data-pm="-1" aria-label="減らす">${icon('minus', 22)}</button>
        <span class="ev-val" id="m-val"></span>
        <button class="ev-pm" type="button" data-pm="1" aria-label="増やす">${icon('plus', 22)}</button>
      </div>
      <div class="dial" id="m-dial">
        <div class="dial__disc"><div class="dial__rot" id="m-rot">
          ${Array.from({ length: sc.count }, (_, q) => {
            const maj = sc.major(q);
            return `<span class="dial__tick" style="transform:rotate(${q * DEG}deg)"><i style="height:${maj ? 22 : 11}px;opacity:${maj ? 1 : 0.45}"></i>${maj ? `<b>${escapeHtml(sc.short(q))}</b>` : ''}</span>`;
          }).join('')}
        </div></div>
        <span class="dial__needle"></span>
      </div>`;
    const dial = box.querySelector('#m-dial');
    const rot = box.querySelector('#m-rot');
    let extra = 0;
    drumUpdate = () => {
      rot.style.transform = `rotate(${-(sc.pos() * DEG) + extra}deg)`;
    };
    let drag = null;
    dial.addEventListener('pointerdown', (e) => {
      try { dial.setPointerCapture(e.pointerId); } catch {}
      drag = { x: e.clientX, p: sc.pos() };
      dial.classList.add('is-drag');
    });
    dial.addEventListener('pointermove', (e) => {
      if (!drag) return;
      const deg = (e.clientX - drag.x) / PX_PER_DEG;
      const np = Math.max(0, Math.min(sc.count - 1, drag.p - Math.round(deg / DEG)));
      extra = deg - (drag.p - np) * DEG;
      if (np !== sc.pos()) { sc.set(np); buzz(4); paintValue(); } else drumUpdate();
    });
    const end = () => { drag = null; extra = 0; dial.classList.remove('is-drag'); drumUpdate(); };
    dial.addEventListener('pointerup', end);
    dial.addEventListener('pointercancel', end);
  }
  box.querySelectorAll('[data-pm]').forEach((b) => {
    holdRepeat(b, () => { bump(Number(b.dataset.pm)); });
  });
}

function bindVerticalDrag(el, getPos, setPos) {
  let d = null;
  el.addEventListener('pointerdown', (e) => {
    try { el.setPointerCapture(e.pointerId); } catch {}
    d = { y: e.clientY, p: getPos() };
    el.classList.add('is-drag');
  });
  el.addEventListener('pointermove', (e) => {
    if (!d) return;
    const np = d.p + Math.round((e.clientY - d.y) / ROW);
    if (np !== getPos()) {
      setPos(np);
      if (getPos() !== d.lastP) { buzz(4); d.lastP = getPos(); }
      paintValue();
    }
  });
  const end = () => { d = null; el.classList.remove('is-drag'); paintValue(); };
  el.addEventListener('pointerup', end);
  el.addEventListener('pointercancel', end);
}

function bump(dir) {
  if (st.mode === 'sek') {
    st.ev = bumpSek(st.ev, dir, sekOpts());
  } else {
    // 刻み（1 / ½ / ⅓ 段）の目盛りに乗せてから 1 目盛り動かす（誤差の積み重なりを防ぐ）
    const per = Math.round(1 / prefs.evStep);
    st.ev = Math.max(-6, Math.min(24, (Math.round(st.ev * per) + dir) / per));
  }
  buzz(4);
  paintValue();
}

function paintValue() {
  drumUpdate?.();
  const val = sheet.el.querySelector('#m-val');
  const hint = sheet.el.querySelector('#m-hint');
  const conv = sheet.el.querySelector('#m-conv');
  const direct = sheet.el.querySelector('#m-ev-input');
  if (direct && document.activeElement !== direct) direct.value = (Math.round(st.ev * 100) / 100).toString();
  const directWrap = sheet.el.querySelector('#m-direct');
  if (directWrap) directWrap.hidden = st.mode === 'sek';
  const sek = st.mode === 'sek';
  if (val) {
    if (sek) {
      const { ib, t } = splitEv(st.ev, sekOpts());
      const v = SSL[ib];
      val.innerHTML = `${escapeHtml(v.startsWith('1/') ? v : `${v}″`)}${st.tenth ? `<small>${t}</small>` : ''}`;
    } else {
      val.textContent = (Math.round(st.ev * 10) / 10).toFixed(1);
    }
  }
  const stepT = prefs.evStep === 1 ? '1' : prefs.evStep === 0.5 ? '½' : '⅓';
  hint.textContent = sek
    ? (st.tenth ? '基準の速度＋1/10 段' : 'メーターの表示に合わせる') + (st.style === 'drum' ? ' · 上下に回す' : ' · 左右に回す')
    : `EV（ISO 100）· ± は ${stepT} 段`;
  conv.textContent = sek ? conversionLine(st.ev, sekOpts()) : (prefs.showApertureSS ? equivalent(st.ev) : '');
}

/** 「EI 320 で f/8 · 1/30s 相当」（設定「絞り・シャッターを併記」） */
function equivalent(ev) {
  const ei = getCurrentShoot()?.ei || 100;
  const eff = ev + Math.log2(ei / 100);
  for (const N of [8, 11, 5.6, 16, 4, 22, 2.8, 32, 45]) {
    if (!APERTURES.includes(N)) continue;
    let best = null;
    SHUTTERS.forEach((s) => {
      const d = Math.abs(Math.log2((N * N) / s.sec) - eff);
      if (!best || d < best.d) best = { s, d };
    });
    if (best && best.d <= 0.5) return `EI ${ei} で f/${N} · ${best.s.label} 相当`;
  }
  return '';
}

// --------------------------------------------------
// 決定・削除
// --------------------------------------------------
function commit(nextId = null) {
  const o = sekOpts();
  let reading = null;
  if (st.mode === 'sek') {
    const { ib, t } = splitEv(st.ev, o);
    reading = {
      mode: 'sek', shutter: SSL[ib], tenths: st.tenth ? t : null,
      step: st.sekStep, aperture: APL[st.ap], iso: Number(isoLabel(o.isoK)),
    };
  }
  st.committed = true;
  pushSpotUndo(st.isNew ? 'ピンの追加' : 'ピンの編集');
  updateSpot(st.id, {
    label: st.label.trim(),
    meterEV: Math.round(st.ev * 1000) / 1000,
    placedZone: st.zone,
    role: st.role,
    reading,
    _draft: false,
  });
  buzz(10);
  rememberLabel(st.label.trim());
  if (nextId) {
    sheet.close().then(() => openMeter(nextId));
    return;
  }
  sheet.close();
}

async function onDelete() {
  if (st.isNew) { sheet.close(); return; }
  const ok = await confirmDialog({ title: `スポット ${st.n} を削除しますか？`, body: 'このピンの測光値・配置ゾーン・役割が消えます。', ok: '削除' });
  if (!ok) return;
  st.committed = true;
  pushSpotUndo('ピンの削除');
  removeSpot(st.id);
  sheet.close();
  toast('ピンを削除しました（測光画面の「取り消す」で戻せます）');
}

/** 5. 使ったラベルを「よく使うラベル」の先頭に加える（最大 12） */
function rememberLabel(label) {
  if (!label || label.length > 20) return;
  const list = prefs.pinLabels.filter((l) => l !== label);
  prefs.pinLabels = [label, ...list].slice(0, 12);
}
